import type { SearchEntry } from "../../redux/features/savedSearchesSlice";
import {
  normalizeSavedSearchUrlInput,
  shouldSkipPersistingSavedSearchUrl,
} from "./normalize-saved-search-url";
import { savedSearchAlertId } from "./saved-search-alert-id";
import type { ServerSavedSearch } from "./server-saved-searches";

/** Mirrors MAX_SAVED_SEARCHES in redux/features/savedSearchesSlice.ts. */
const MAX_SAVED_SEARCHES = 50;

/**
 * How long a local search must sit untouched before the account-sync pass writes it through to
 * the server. This is a short "stopped typing/clicking" pause, not a "the user is done with this
 * search forever" wait: closing the tab before this elapses would lose that last edit, so it's
 * kept small. It's still needed (rather than syncing on every state change) because
 * SAVE_COALESCE_WINDOW_MS in contexts/SearchContext.tsx keeps updating the SAME local entry's
 * timestamp while the user is actively refining (each keystroke/filter click), and without a
 * debounce here that would mean a network write per keystroke.
 *
 * This does not by itself stop one continuous refinement session (e.g. "head" -> "head of" ->
 * "head of engineering") from touching more than one server item over its lifetime — each
 * settled pause targets a different hash-of-url item key. useSavedSearchAccountSync.ts handles
 * that: it tracks the single most-recently-uploaded item as the "open session" and deletes it
 * once the local list shows its URL was refined away, so only the latest URL in an in-progress
 * session has a live server item at any moment.
 */
export const SAVE_SYNC_DEBOUNCE_MS = 8_000;

/** Canonical stored form: path + search, origin stripped (same basis as savedSearchAlertId). */
export function canonicalSavedSearchPath(storedUrl: string): string {
  return toPathAndSearch(storedUrl);
}

function toPathAndSearch(storedUrl: string): string {
  const normalized = normalizeSavedSearchUrlInput(storedUrl) || storedUrl.trim();
  try {
    const parsed = new URL(normalized, "https://www.jobcrawls.com");
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return normalized;
  }
}

export interface SavedSearchUpload {
  id: string;
  url: string;
  timestamp: number;
}

export interface SavedSearchSyncPlan {
  /** Local searches with no server item, settled long enough to upload — to PUT (version 0). */
  uploads: SavedSearchUpload[];
  /** Union of local + server for setSavedSearches, newest first, capped. */
  mergedLocal: SearchEntry[];
  /** mergedLocal differs from the local list — dispatch only then. */
  changed: boolean;
  /**
   * Earliest epoch ms at which a currently-unsettled, not-yet-uploaded local search would
   * become upload-eligible; null if nothing is pending. Callers use this to schedule a retry
   * pass instead of waiting for the next unrelated remount to notice the entry has settled.
   */
  nextSettleCheckAt: number | null;
}

/**
 * One-shot account sync for saved searches (spec §8): upload what the account
 * is missing, pull what other devices saved. Deletions are not reconciled here
 * (no tombstones) — the management UI deletes both sides directly.
 *
 * `now` is injectable for tests; callers should otherwise leave it as the default.
 */
export function computeSavedSearchSyncPlan(
  local: SearchEntry[],
  server: ServerSavedSearch[],
  now: number = Date.now()
): SavedSearchSyncPlan {
  const serverIds = new Set(server.map((s) => s.id));

  const normalizedLocal: SearchEntry[] = [];
  const seenLocalIds = new Set<string>();
  for (const entry of local) {
    if (typeof entry?.url !== "string") continue;
    const normalizedUrl = toPathAndSearch(entry.url);
    if (shouldSkipPersistingSavedSearchUrl(normalizedUrl)) continue;
    const id = savedSearchAlertId(normalizedUrl);
    if (seenLocalIds.has(id)) continue;
    seenLocalIds.add(id);
    normalizedLocal.push({ url: normalizedUrl, timestamp: entry.timestamp });
  }

  const unsynced = normalizedLocal.filter((entry) => !serverIds.has(savedSearchAlertId(entry.url)));

  const uploads: SavedSearchUpload[] = unsynced
    .filter((entry) => now - entry.timestamp >= SAVE_SYNC_DEBOUNCE_MS)
    .map((entry) => ({ id: savedSearchAlertId(entry.url), url: entry.url, timestamp: entry.timestamp }));

  const pendingUnsettled = unsynced.filter((entry) => now - entry.timestamp < SAVE_SYNC_DEBOUNCE_MS);
  const nextSettleCheckAt = pendingUnsettled.length
    ? Math.min(...pendingUnsettled.map((entry) => entry.timestamp + SAVE_SYNC_DEBOUNCE_MS))
    : null;

  const pulls: SearchEntry[] = server
    .filter((s) => !seenLocalIds.has(s.id) && !shouldSkipPersistingSavedSearchUrl(s.url))
    .map((s) => ({
      url: toPathAndSearch(s.url),
      timestamp: typeof s.createdAt === "number" ? s.createdAt : 0,
    }));

  const mergedLocal = [...normalizedLocal, ...pulls]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_SAVED_SEARCHES);

  const changed =
    mergedLocal.length !== local.length ||
    mergedLocal.some((entry, index) => local[index]?.url !== entry.url);

  return { uploads, mergedLocal, changed, nextSettleCheckAt };
}
